import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useSEO, generateLocalBusinessSchema, generateServiceSchema } from '../../utils/seo';

export default function ListingCliftonPage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  // SEO Configuration
  useSEO({
    title: 'Clifton Luxury Painters - High-End Beach House Painting | Villa Specialists',
    description: 'Luxury painting services in Clifton, Camps Bay, and Bantry Bay. Specialized in high-end beach houses and luxury villas. Over 17 years serving Atlantic Seaboard with premium finishes.',
    keywords: 'painters Clifton, luxury painters Clifton, beach house painting, villa painting, high-end painting, Atlantic Seaboard painters',
    schema: {
      '@context': 'https://schema.org',
      '@graph': [
        generateLocalBusinessSchema({
          name: 'Painters Clifton',
          description: 'Luxury beach house and villa painting services',
          address: 'Clifton, Cape Town',
          priceRange: '$$$',
          serviceArea: ['Clifton', 'Camps Bay', 'Bantry Bay', 'Fresnaye', 'Llandudno']
        }),
        generateServiceSchema({
          name: 'Luxury Beach House Painting',
          description: 'High-end beach house and villa painting',
          provider: 'Painters Clifton',
          areaServed: ['Clifton', 'Camps Bay', 'Bantry Bay'],
          serviceType: ['Beach House Painting', 'Villa Painting', 'Luxury Finishes']
        })
      ]
    }
  });

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const listing = {
    name: "Painters Clifton",
    location: "Clifton, Cape Town",
    website: "https://paintersclifton.co.za",
    heroImage: "https://public.readdy.ai/ai/img_res/60e892f24ae1892be0ce11b2d101a0e8.jpg",
    gallery: [
      "https://readdy.ai/api/search-image?query=freshly%20painted%20luxury%20home%20exterior%20with%20white%20walls%20and%20contemporary%20design%20ocean%20views%20professional%20finish%20in%20coastal%20setting%20with%20blue%20sky&width=800&height=600&seq=gallery-clifton-1&orientation=landscape",
      "https://readdy.ai/api/search-image?query=elegant%20interior%20room%20painting%20with%20designer%20finishes%20professional%20painter%20applying%20paint%20to%20walls%20luxury%20home%20setting%20with%20ocean%20views&width=800&height=600&seq=gallery-clifton-2&orientation=landscape",
      "https://readdy.ai/api/search-image?query=luxury%20villa%20exterior%20painting%20project%20with%20scaffolding%20professional%20painters%20working%20on%20beachfront%20property%20modern%20coastal%20architecture&width=800&height=600&seq=gallery-clifton-3&orientation=landscape",
      "https://readdy.ai/api/search-image?query=beautifully%20painted%20luxury%20interior%20living%20room%20with%20designer%20finishes%20elegant%20color%20scheme%20high-end%20furniture%20ocean%20views%20natural%20lighting&width=800&height=600&seq=gallery-clifton-4&orientation=landscape"
    ],
    services: [
      {
        title: "Luxury Home Painting",
        description: "Specialized painting services for luxury homes and villas in Clifton's exclusive beachfront location. We understand the high standards required for premium properties and deliver exceptional results.",
        features: ["High-end interior finishes", "Designer color consultation", "Premium paint brands", "Attention to detail"]
      },
      {
        title: "Coastal Weather Protection",
        description: "Expert coastal painting solutions using marine-grade paints designed to withstand salt air, wind, and harsh coastal conditions. Protect your investment with our specialized coatings.",
        features: ["Marine-grade paints", "Salt-resistant coatings", "UV protection", "Weather-resistant finishes"]
      },
      {
        title: "Interior Design Painting",
        description: "Collaborate with interior designers to achieve the perfect finish for your luxury home. We specialize in decorative finishes, feature walls, and custom color matching.",
        features: ["Designer collaboration", "Custom color matching", "Decorative finishes", "Feature wall specialists"]
      }
    ],
    serviceAreas: ["Clifton", "Camps Bay", "Bantry Bay", "Fresnaye", "Sea Point", "Bakoven"],
    about: "Painters Clifton is a premium painting company specializing in luxury homes and beachfront properties in Clifton and the Atlantic Seaboard. With over 14 years of experience in high-end residential painting, we have established ourselves as the trusted choice for discerning homeowners who demand excellence. Our team of skilled artisans understands the unique requirements of luxury coastal properties and delivers impeccable results that enhance property value and aesthetic appeal.",
    aboutExtended: [
      "We specialize in luxury residential painting, offering comprehensive solutions that cover everything from initial designer consultation to final finishing touches. Our expertise extends across interior painting with designer finishes, exterior coastal protection painting, decorative techniques, and specialized marine-grade coatings. We work closely with homeowners, interior designers, and architects to bring their vision to life with precision and artistry.",
      "What sets us apart is our commitment to using only the finest quality paints and materials from premium international brands, specifically selected for their performance in harsh coastal environments. Our painters are highly skilled craftsmen with extensive experience in luxury properties, fully insured, and dedicated to maintaining the highest standards of workmanship and professionalism.",
      "We understand that your Clifton property is a significant investment, which is why we take extraordinary care to protect your home and furnishings throughout the painting process. Whether you're refreshing a beachfront villa or undertaking a complete luxury renovation, we deliver exceptional results that exceed the highest expectations and stand the test of time in this demanding coastal environment."
    ],
    ourApproach: [
      "Detailed consultation with homeowners and designers",
      "Comprehensive surface preparation and repairs",
      "Premium luxury-grade paints and materials",
      "Expert application by skilled craftsmen",
      "Meticulous protection of property and furnishings",
      "Final inspection and quality assurance"
    ],
    whyChoose: [
      "14+ years luxury home experience",
      "Coastal painting specialists",
      "Premium international paint brands",
      "Designer collaboration expertise",
      "Fully insured and certified",
      "Excellence guaranteed"
    ],
    testimonials: [
      {
        name: "Alexandra Sinclair",
        rating: 5,
        text: "Exceptional service from start to finish. The team understood our vision perfectly and delivered a flawless result. Our Clifton home has never looked better!"
      },
      {
        name: "Richard Pemberton",
        rating: 5,
        text: "Painters Clifton are true professionals. They worked seamlessly with our interior designer and the quality of their work is outstanding. Highly recommended for luxury properties."
      },
      {
        name: "Catherine du Toit",
        rating: 5,
        text: "Impeccable attention to detail and the highest quality finishes. They protected our home beautifully during the work and the results are simply stunning."
      }
    ]
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className={`sticky top-0 z-50 border-b border-gray-800 transition-all duration-300 ${isScrolled ? 'bg-black text-white' : 'bg-black text-white'}`}>
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="text-3xl font-bold whitespace-nowrap">
              <span className="text-white">PAINTE</span>
              <span className="text-[#e9f638]">RRR</span>
            </Link>
            <nav className="hidden md:flex items-center space-x-8">
              <Link to="/" className="hover:text-[#e9f638] transition whitespace-nowrap">Home</Link>
              <Link to="/about" className="hover:text-[#e9f638] transition whitespace-nowrap">About</Link>
              <Link to="/contact" className="bg-[#e9f638] text-black px-6 py-2 rounded-lg font-semibold hover:bg-white transition whitespace-nowrap cursor-pointer">Get a Quote</Link>
            </nav>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <div className="relative h-96 w-full overflow-hidden">
        <img 
          src={listing.heroImage}
          alt={listing.name}
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent"></div>
        <div className="absolute bottom-0 left-0 right-0 container mx-auto px-4 pb-8">
          <h1 className="text-5xl font-bold text-white mb-2">{listing.name}</h1>
          <p className="text-xl text-gray-200 flex items-center">
            <i className="ri-map-pin-line mr-2"></i>
            {listing.location}
          </p>
        </div>
      </div>

      {/* Contact Bar */}
      <div className="bg-[#e9f638] py-4">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-center items-center gap-4">
            <a 
              href={listing.website}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-black text-white px-8 py-3 rounded-lg font-bold hover:bg-gray-800 transition whitespace-nowrap cursor-pointer"
            >
              Visit Website
            </a>
          </div>
        </div>
      </div>

      {/* Tabs Navigation */}
      <div className="bg-gray-50 border-b border-gray-200 sticky top-[73px] z-40">
        <div className="container mx-auto px-4">
          <div className="flex space-x-8 overflow-x-auto">
            <button
              onClick={() => setActiveTab('overview')}
              className={`py-4 px-2 font-semibold whitespace-nowrap border-b-2 transition cursor-pointer ${
                activeTab === 'overview' ? 'border-[#e9f638] text-black' : 'border-transparent text-gray-500 hover:text-black'
              }`}
            >
              Overview
            </button>
            <button
              onClick={() => setActiveTab('services')}
              className={`py-4 px-2 font-semibold whitespace-nowrap border-b-2 transition cursor-pointer ${
                activeTab === 'services' ? 'border-[#e9f638] text-black' : 'border-transparent text-gray-500 hover:text-black'
              }`}
            >
              Services
            </button>
            <button
              onClick={() => setActiveTab('gallery')}
              className={`py-4 px-2 font-semibold whitespace-nowrap border-b-2 transition cursor-pointer ${
                activeTab === 'gallery' ? 'border-[#e9f638] text-black' : 'border-transparent text-gray-500 hover:text-black'
              }`}
            >
              Gallery
            </button>
            <button
              onClick={() => setActiveTab('reviews')}
              className={`py-4 px-2 font-semibold whitespace-nowrap border-b-2 transition cursor-pointer ${
                activeTab === 'reviews' ? 'border-[#e9f638] text-black' : 'border-transparent text-gray-500 hover:text-black'
              }`}
            >
              Reviews
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            {activeTab === 'overview' && (
              <div>
                <section className="mb-12">
                  <h2 className="text-3xl font-bold mb-6">About Painters Clifton</h2>
                  <p className="text-gray-700 text-lg leading-relaxed mb-4">{listing.about}</p>
                  {listing.aboutExtended.map((paragraph, index) => (
                    <p key={index} className="text-gray-700 text-lg leading-relaxed mb-4">{paragraph}</p>
                  ))}
                </section>

                <section className="mb-12">
                  <h3 className="text-2xl font-bold mb-6">Our Professional Approach</h3>
                  <div className="bg-gray-50 p-6 rounded-lg border-2 border-gray-200">
                    <ul className="space-y-3">
                      {listing.ourApproach.map((step, index) => (
                        <li key={index} className="flex items-start">
                          <i className="ri-checkbox-circle-line text-[#e9f638] text-2xl mr-3 mt-1"></i>
                          <span className="text-gray-700 text-lg">{step}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </section>

                <section className="mb-12">
                  <h3 className="text-2xl font-bold mb-6">Why Choose Painters Clifton</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {listing.whyChoose.map((reason, index) => (
                      <div key={index} className="flex items-start">
                        <i className="ri-check-line text-[#e9f638] text-2xl mr-3 mt-1"></i>
                        <span className="text-gray-700 text-lg">{reason}</span>
                      </div>
                    ))}
                  </div>
                </section>

                <section>
                  <h3 className="text-2xl font-bold mb-6">Areas We Serve</h3>
                  <p className="text-gray-700 text-lg mb-4">We proudly serve the following areas on the Atlantic Seaboard:</p>
                  <div className="flex flex-wrap gap-3">
                    {listing.serviceAreas.map((area, index) => (
                      <span key={index} className="bg-gray-100 text-black px-4 py-2 rounded-full font-semibold">
                        {area}
                      </span>
                    ))}
                  </div>
                </section>
              </div>
            )}

            {activeTab === 'services' && (
              <div>
                <h2 className="text-3xl font-bold mb-4">Our Professional Painting Services</h2>
                <p className="text-gray-700 text-lg mb-8">We offer a comprehensive range of painting services tailored to meet your specific needs and requirements.</p>
                <div className="space-y-8">
                  {listing.services.map((service, index) => (
                    <div key={index} className="bg-gray-50 p-6 rounded-lg border-2 border-gray-200 hover:border-[#e9f638] transition">
                      <h3 className="text-2xl font-bold mb-3">{service.title}</h3>
                      <p className="text-gray-700 mb-4 text-lg">{service.description}</p>
                      <h4 className="font-semibold text-lg mb-3">What We Offer:</h4>
                      <ul className="space-y-2">
                        {service.features.map((feature, idx) => (
                          <li key={idx} className="flex items-center text-gray-700">
                            <i className="ri-arrow-right-s-line text-[#e9f638] mr-2"></i>
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'gallery' && (
              <div>
                <h2 className="text-3xl font-bold mb-8">Project Gallery</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {listing.gallery.map((image, index) => (
                    <div key={index} className="h-80 w-full overflow-hidden rounded-lg shadow-lg hover:shadow-2xl transition">
                      <img 
                        src={image}
                        alt={`Project ${index + 1}`}
                        className="w-full h-full object-cover object-top hover:scale-110 transition-transform duration-500 cursor-pointer"
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'reviews' && (
              <div>
                <h2 className="text-3xl font-bold mb-8">Customer Reviews</h2>
                <div className="space-y-6">
                  {listing.testimonials.map((testimonial, index) => (
                    <div key={index} className="bg-gray-50 p-6 rounded-lg border-2 border-gray-200">
                      <div className="flex items-center mb-4">
                        <div className="flex mr-4">
                          {[...Array(testimonial.rating)].map((_, i) => (
                            <i key={i} className="ri-star-fill text-[#e9f638] text-xl"></i>
                          ))}
                        </div>
                        <span className="font-bold text-lg">{testimonial.name}</span>
                      </div>
                      <p className="text-gray-700 text-lg leading-relaxed">{testimonial.text}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-lg p-6 sticky top-24">
              <h3 className="text-2xl font-bold mb-6">Contact Information</h3>
              
              <div className="space-y-4 mb-6">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center">
                    <i className="ri-map-pin-line text-2xl text-[#e9f638]"></i>
                  </div>
                  <div>
                    <p className="font-semibold">Service Areas</p>
                    <p className="text-gray-600">Clifton, Camps Bay, Bantry Bay</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center">
                    <i className="ri-time-line text-2xl text-[#e9f638]"></i>
                  </div>
                  <div>
                    <p className="font-semibold">Business Hours</p>
                    <p className="text-gray-600">Mon-Fri: 8AM-5PM</p>
                    <p className="text-gray-600">Sat: 9AM-1PM</p>
                  </div>
                </div>
              </div>
              
              <a 
                href={listing.website}
                target="_blank"
                rel="noopener noreferrer"
                className="block w-full bg-[#e9f638] text-black text-center py-3 rounded-lg font-bold hover:bg-black hover:text-[#e9f638] transition-colors duration-300 whitespace-nowrap cursor-pointer"
              >
                Visit Website
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-black text-white border-t border-gray-800">
        <div className="container mx-auto px-4 py-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            <div>
              <h3 className="text-2xl font-bold mb-4">
                <span className="text-white">PAINTE</span>
                <span className="text-[#e9f638]">RRR</span>
              </h3>
              <p className="text-gray-400">South Africa's premier directory for professional house painters.</p>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-[#e9f638]">Quick Links</h4>
              <ul className="space-y-2">
                <li><Link to="/" className="text-gray-400 hover:text-[#e9f638] cursor-pointer">Home</Link></li>
                <li><Link to="/about" className="text-gray-400 hover:text-[#e9f638] cursor-pointer">About</Link></li>
                <li><Link to="/contact" className="text-gray-400 hover:text-[#e9f638] cursor-pointer">Contact</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-[#e9f638]">Service Areas</h4>
              <ul className="space-y-2 text-gray-400">
                <li>Table View</li>
                <li>Melkbosstrand</li>
                <li>Durbanville</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-[#e9f638]">Connect</h4>
              <p className="text-gray-400">Find the perfect painter for your project today.</p>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 flex flex-col md:flex-row justify-between items-center text-sm text-gray-400">
            <p>© 2025 PAINTERRR. All rights reserved.</p>
            <div className="flex space-x-4 mt-4 md:mt-0">
              <a href="https://readdy.ai/?origin=logo" target="_blank" rel="noopener noreferrer" className="hover:text-[#e9f638] cursor-pointer">
                Powered by Readdy
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
